import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  Injector,
  linkedSignal,
  OnDestroy,
  OnInit,
  Optional,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core';
import {
  Application,
  ApplicationStore,
  LinkModule,
  Refreshable,
  RefreshService,
  SettingsStore,
  SOFTLINE_FEATURE_APPLICATIONS,
  SOFTLINE_FEATURE_SETTINGS,
  title,
} from '@softline/application';
import { AuthorizationStore, SOFTLINE_FEATURE_AUTHORIZATION } from '@softline/auth';
import { isDefinedNotEmpty, Store } from '@softline/core';
import {
  SOFTLINE_FEATURE_TRANSLATION,
  TranslationStore,
  UiCoreModule,
} from '@softline/ui-core';
import { Observable } from 'rxjs';
import { SOFTLINE_CONFIG_MODULES_STATIC_LINKS } from '../module.providers';
import { ModuleSettings } from '../module.settings';
import {
  SOFTLINE_CONFIG_APP_OVERVIEW,
  SOFTLINE_SETTINGS_APP_OVERVIEW,
} from '../module.shared';
import { AppGridComponent } from './app-grid/app-grid.component';
import { AppListComponent } from './app-list/app-list.component';

@Component({
  selector: 'soft-app-overview',
  templateUrl: './app-overview.component.html',
  styleUrls: ['./app-overview.component.css'],
  imports: [
    CommonModule,
    UiCoreModule,
    AppGridComponent,
    AppListComponent,
    PageComponent,
    PageHeaderComponent,
    CdkAccordion,
    CdkAccordionItem,
  ],
})
export class AppOverviewComponent implements OnInit, OnDestroy, Refreshable {
  readonly injector = inject(Injector);

  public readonly config = inject(SOFTLINE_CONFIG_APP_OVERVIEW, {
    optional: true,
  });

  isGrouped = signal(
    this.config?.isGroupButtonActive && this.config?.defaultView === 'grouped'
      ? true
      : false
  );

  newSearch = signal<string | undefined | null>('');

  nonPermittedAppsPermission = computed(() => {
    const isActive =
      this.config?.isNonPermittedAppsButtonActive ||
      this.permissions()?.includes('SOFTAPPS.MODULE.VIEW_NON_PERMITTED_APPS');
    return isActive;
  });

  disableNoPermissionApps = computed(() => {
    return this.config?.disableNoPermissionApps;
  });

  modulesSortedByAlphabet = computed(() => {
    return isDefinedNotEmpty(this.config?.isModulesSortedByAlphabet)
      ? this.config?.isModulesSortedByAlphabet
      : true;
  });

  showNonPermittedApps = signal(false);

  settings$ = this.store.observe(
    SOFTLINE_FEATURE_SETTINGS,
    SettingsStore.getters.values<ModuleSettings>(),
    SOFTLINE_SETTINGS_APP_OVERVIEW
  );

  private getTranslatedName(name: string): string {
    return this.store.get(
      SOFTLINE_FEATURE_TRANSLATION,
      TranslationStore.getters.translation,
      name
    );
  }

  search = '';

  applications$: Observable<readonly Application[]> = this.store.observe(
    SOFTLINE_FEATURE_APPLICATIONS,
    ApplicationStore.getters.items
  );

  readonly linkModules = signal<LinkModule[]>([]);
  readonly appModules = toSignal(this.applications$, {
    initialValue: [] as Application[],
  });

  readonly applications = computed(() => {
    const showNonPermitted = this.showNonPermittedApps();
    const disableNoPermissionApps = this.disableNoPermissionApps();
    const userPerms = (this.permissions() ?? []).map((p) => p.trim());

    const apps = [...this.appModules(), ...this.linkModules()].map((app) => {
      const perm = (app.permission ?? '').trim();
      if (perm.length === 0) {
        return {
          ...app,
          disabled: disableNoPermissionApps ? true : false,
        } as Application & { disabled?: boolean };
      }
      const hasPermission = userPerms.includes(perm);
      return {
        ...app,
        disabled: !hasPermission,
      } as Application & { disabled?: boolean };
    });

    const visible = showNonPermitted ? apps : apps.filter((a) => !a.disabled);

    let sorted = visible;

    if (this.modulesSortedByAlphabet()) {
      sorted = visible.sort((a, b) =>
      this.getTranslatedName(a.name).localeCompare(
        this.getTranslatedName(b.name),
        undefined,
        { sensitivity: 'base' }
      )
      );
    }

      const search = this.newSearch();
    if (!search || search.length === 0) {
      return sorted;
    }
    const searchTerm = search.toLowerCase();
    return sorted.filter((app) => {
      const nameMatches = this.getTranslatedName(app.name)
        .toLowerCase()
        .includes(searchTerm);
      const descriptionMatches =
        app.description?.toLowerCase().includes(searchTerm) ?? false;
      const groupMatches = app.group?.name?.toLowerCase().includes(searchTerm) ?? false;
      return nameMatches || descriptionMatches || groupMatches;
    });
  });

  readonly moduleGroups = linkedSignal(() => {
    const groups: {
      id: number;
      name: string;
      icon?: string;
      applications: Application[];
      expansion: boolean;
    }[] = [{ id: 0, name: 'none', applications: [], expansion: true }];

    for (const app of this.applications()) {
      const groupName = app.group?.name || 'none';
      const groupId = app.group?.id || 0;

      let group = groups.find((g) => g.name === groupName);
      if (!group) {
        group = {
          id: groupId,
          name: groupName,
          applications: [],
          expansion: true,
          icon: app.group?.icon,
        };
        groups.push(group);
      }

      group.applications.push(app);
    }

    const noneIdx = groups.findIndex((g) => g.name === 'none');
    if (noneIdx > -1 && groups[noneIdx].applications.length === 0) {
      groups.splice(noneIdx, 1);
    }

    return groups.sort((a, b) => {
      if (a.name === 'none') return -1;
      if (b.name === 'none') return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  });

  setExpansion(groupId: number, expansion: boolean): void {
    const groups = this.moduleGroups();
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      group.expansion = expansion;
    }
    this.moduleGroups.set(groups);
  }

  permissions$: Observable<readonly string[]> = this.store.observe(
    SOFTLINE_FEATURE_AUTHORIZATION,
    AuthorizationStore.getters.permissions
  );

  permissions = toSignal(this.permissions$);

  constructor(
    public store: Store,
    protected router: Router,
    private refreshService: RefreshService,
    @Optional()
    @Inject(SOFTLINE_CONFIG_MODULES_STATIC_LINKS)
    _staticLinks?: LinkModule[]
  ) {
    this.linkModules.set(_staticLinks ?? []);
    title('Alle Applikationen');
  }

  async ngOnInit(): Promise<void> {
    this.refreshService.add(this);

    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.loadOnce
    );

    await this.store.dispatch(SOFTLINE_FEATURE_SETTINGS, SettingsStore.actions.load);

    await this.setMode('grid');
  }

  ngOnDestroy(): void {
    this.refreshService.remove(this);
  }

  async refresh(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.actions.load
    );
  }

  filterPermissions(
    application: Application,
    permissions: readonly string[] | undefined
  ): boolean {
    return (
      !application.permission || (permissions ?? []).indexOf(application.permission) > -1
    );
  }

  filterInput(
    application: Application,
    param?: { store: Store; input: string }
  ): boolean {
    if (!param) return false;
    const feature = SOFTLINE_FEATURE_TRANSLATION;
    const selector = TranslationStore.getters.translation;
    return param.store
      .get(feature, selector, application.name)
      .toLowerCase()
      .startsWith(param.input.toLowerCase());
  }

  async navigate(application: Application): Promise<void> {
    if (application?.type === 'link') {
      if (application?.['execute']) {
        await application.execute(this.injector);
      } else {
        window.open(application.href, '_blank');
      }
    } else {
      await this.router.navigate([application.route], application.routeParams);
    }
  }

  async setMode(mode: 'grid' | 'list'): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_SETTINGS,
      SettingsStore.mutations.setKey<ModuleSettings>(),
      { key: SOFTLINE_SETTINGS_APP_OVERVIEW, values: { display: mode } }
    );
    await this.store.dispatch(SOFTLINE_FEATURE_SETTINGS, SettingsStore.actions.save, {
      display: mode,
    });
  }
}
